//! Single source of truth for uploadable media types, shared by the browser
//! (file-picker `accept`) and the server (signed-URL extension allowlist).
import { z } from 'zod';
const imageMedia = [
    {
        mime: 'image/jpeg',
        extensions: ['jpg', 'jpeg', 'jpe', 'jif', 'jfif', 'jfi'],
    },
    { mime: 'image/png', extensions: ['png'] },
    { mime: 'image/webp', extensions: ['webp'] },
];
const videoMedia = [
    { mime: 'video/mp4', extensions: ['mp4'] },
    { mime: 'video/webm', extensions: ['webm'] },
    { mime: 'video/quicktime', extensions: ['mov', 'qt'] },
];
const applicationMedia = [
    { mime: 'application/pdf', extensions: ['pdf'] },
];
const allowedMedia = [
    ...imageMedia,
    ...videoMedia,
    ...applicationMedia,
];
/** The canonical extension for an allowed mime type, `null` for anything else. */
export const getFileExtensionFromMime = (mime) => allowedMedia.find((media) => media.mime === mime)?.extensions[0] ?? null;
export const AllowedUploadMimeTypeLabel = {
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/webp': 'WEBP',
    'video/mp4': 'MP4',
    'video/webm': 'WebM',
    'video/quicktime': 'QuickTime',
    'application/pdf': 'PDF',
};
export const imageTypes = imageMedia.map((media) => media.mime);
export const videoTypes = videoMedia.map((media) => media.mime);
export const applicationTypes = applicationMedia.map((media) => media.mime);
export const defaultAcceptTypes = allowedMedia.map((media) => media.mime);
export const UploadContentType = {
    image: 'image',
    video: 'video',
    pdf: 'pdf',
};
export const UploadContentTypeLabel = {
    image: 'Images',
    video: 'Videos',
    pdf: 'PDFs',
};
export const uploadContentTypesFileNoun = (contentTypes) => {
    const [contentType, ...others] = contentTypes;
    return !others.length && contentType !== UploadContentType.pdf
        ? `${contentType} file`
        : 'file';
};
export const uploadContentTypeMimeTypes = {
    image: imageTypes,
    video: videoTypes,
    pdf: applicationTypes,
};
export const allowedUploadExtensions = new Set(allowedMedia.flatMap((media) => media.extensions));
/** Path separators, percent-encoding, and control/format chars — the latter covers NUL truncation and bidi overrides that spoof the extension. */
const unsafeFilenameChars = /[\p{C}/\\%]/u;
/** Schema allowing any non-hostile filename ending in an allowed extension. Storage keys are sanitized separately, see `makeUrlSafe`. */
export const allowedFilename = z
    .string()
    .trim()
    .nonempty()
    .max(255)
    .transform((filename) => ({
    filename,
    ext: /\.([^.\s]+)$/.exec(filename)?.[1]?.toLowerCase(),
}))
    .refine(({ filename }) => !unsafeFilenameChars.test(filename) &&
    !filename.includes('..') &&
    // a leading dot hides the file, a leading hyphen reads as a CLI flag
    !/^[.-]/.test(filename), { error: 'Invalid filename' })
    .refine(({ ext }) => ext && allowedUploadExtensions.has(ext), {
    error: 'Disallowed file type', // ? possibly include extension
})
    .transform(({ filename }) => filename);
export const isAllowedFilename = (filename) => allowedFilename.safeParse(filename).success;
//# sourceMappingURL=uploadMedia.js.map